import { useState, useEffect, useCallback, useRef } from "react"; // Added useRef
import { listCategoryGroups } from "../services/categoryGroupApis";

export const GroupPurposeType = {
  SELLER_SUGGESTION: "SELLER_SUGGESTION",
  SEO_PAGE: "SEO_PAGE",
  INDUSTRY_SPECIFIC: "INDUSTRY_SPECIFIC", // Matches your backend update
  BUYER_EMAIL_MARKETING_LIST: "BUYER_EMAIL_MARKETING_LIST",
  SELLER_EMAIL_MARKETING_LIST: "SELLER_EMAIL_MARKETING_LIST",
  DIRECTORY_LISTING: "DIRECTORY_LISTING",
  INTERNAL_TAGGING: "INTERNAL_TAGGING",
  CAMPAIGN_SPECIFIC: "CAMPAIGN_SPECIFIC",
};

export function useCategoryGroupsData(
  initialIsActive = true,
  includeChildren = true,
  locale
) {
  const [groupsByActivity, setGroupsByActivity] = useState({
    active: [],
    inactive: [],
  });
  const [currentView, setCurrentView] = useState(
    initialIsActive ? "active" : "inactive"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Keep track of whether data has been fetched for each tab to avoid redundant initial fetches
  const hasFetched = useRef({ active: false, inactive: false });

  // Memoized fetch function
  const fetchGroupsForView = useCallback(
    async (viewToFetch) => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          is_active: viewToFetch === "active",
          include_children: includeChildren,
          limit: 500,
          locale: locale,
        };
        const response = await listCategoryGroups(params);
        const fetchedData = response.data || [];

        setGroupsByActivity((prev) => ({
          ...prev,
          [viewToFetch]: fetchedData,
        }));
        localStorage.setItem(
          `categoryGroups_${viewToFetch}`,
          JSON.stringify(fetchedData)
        );
        hasFetched.current[viewToFetch] = true; // Mark as fetched
      } catch (err) {
        console.error(`Failed to fetch ${viewToFetch} category groups:`, err);
        setError(err.message || `Failed to fetch ${viewToFetch} groups`);
        // Potentially clear only the specific view's data on error
        setGroupsByActivity((prev) => ({ ...prev, [viewToFetch]: [] }));
      } finally {
        setLoading(false);
      }
    },
    [includeChildren, locale]
  ); // includeChildren is a stable prop from the parent

  // Effect to load initial data or when currentView (tab) changes
  useEffect(() => {
    setCurrentView(initialIsActive ? "active" : "inactive");
  }, [initialIsActive]);

  useEffect(() => {
    // Try to load from localStorage first
    const cached = localStorage.getItem(`categoryGroups_${currentView}`);
    if (cached) {
      try {
        const parsedCache = JSON.parse(cached);
        setGroupsByActivity((prev) => ({
          ...prev,
          [currentView]: parsedCache,
        }));
        hasFetched.current[currentView] = true; // Assume cache means it was "fetched" at some point
        // If you want to ensure freshness beyond simple caching, you might add a timestamp to cache
        // and refetch if older than X minutes, but that adds complexity.
      } catch (e) {
        localStorage.removeItem(`categoryGroups_${currentView}`);
        // If cache is invalid and this view hasn't been fetched in this session, fetch it.
        if (!hasFetched.current[currentView]) {
          fetchGroupsForView(currentView);
        }
      }
    } else {
      // If no cache and not yet fetched in this session for this view, fetch it.
      if (!hasFetched.current[currentView]) {
        fetchGroupsForView(currentView);
      }
    }
  }, [currentView, fetchGroupsForView]); // fetchGroupsForView is memoized

  // Functions to update local state after CRUD without full refetch (optimistic or confirmed updates)
  const updateGroupInState = useCallback((updatedGroup) => {
    setGroupsByActivity((prev) => {
      // Ensure we're working with copies to avoid direct state mutation
      let activeGroups = [...(prev.active || [])];
      let inactiveGroups = [...(prev.inactive || [])];

      // Remove the group from both lists first to handle moves and updates cleanly
      activeGroups = activeGroups.filter((g) => g.id !== updatedGroup.id);
      inactiveGroups = inactiveGroups.filter((g) => g.id !== updatedGroup.id);

      // Add the updated group to the correct list based on its new status
      if (updatedGroup.is_active) {
        activeGroups.push(updatedGroup);
        // Sort if needed, e.g., activeGroups.sort((a, b) => a.name.localeCompare(b.name));
      } else {
        inactiveGroups.push(updatedGroup);
        // Sort if needed
      }

      // Update localStorage for both views
      localStorage.setItem(
        "categoryGroups_active",
        JSON.stringify(activeGroups)
      );
      localStorage.setItem(
        "categoryGroups_inactive",
        JSON.stringify(inactiveGroups)
      );

      // Return the new state for groupsByActivity
      return { active: activeGroups, inactive: inactiveGroups };
    });
  }, []); // No dependencies needed if it only uses setGroupsByActivity

  // Function to explicitly refresh data for the current view (e.g., after CRUD)
  const refreshCurrentViewGroups = useCallback(() => {
    localStorage.removeItem(`categoryGroups_${currentView}`);
    hasFetched.current[currentView] = false; // Reset fetched status to force a new API call
    fetchGroupsForView(currentView);
  }, [currentView, fetchGroupsForView]);

  const removeGroupFromState = useCallback((groupId, wasActive) => {
    const view = wasActive ? "active" : "inactive";
    setGroupsByActivity((prev) => {
      const newViewState = (prev[view] || []).filter((g) => g.id !== groupId);
      localStorage.setItem(
        `categoryGroups_${view}`,
        JSON.stringify(newViewState)
      );
      return { ...prev, [view]: newViewState };
    });
  }, []);

  return {
    groups: groupsByActivity[currentView] || [], // Groups for the current active tab
    allFetchedGroups: groupsByActivity, // If needed to access both active/inactive together
    loading,
    error,
    currentView, // 'active' or 'inactive'
    setCurrentView, // To be called by Tab onClick
    refreshGroups: refreshCurrentViewGroups, // Renamed for clarity
    updateGroupInState,
    removeGroupFromState,
    // setGroups is more complex now, direct manipulation should be through update/remove
  };
}
